import type React from "react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users } from "lucide-react";

import {
  formatDateRange,
  type InviteLink,
} from "@travelix/core";

import { useAuth } from "@/lib/auth-provider";
import { NavHeader } from "@/components/nav-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { getInviteLink, redeemInviteLink, unwrap, queryKeys } from "@/lib/api";

/**
 * Redeem-invite-link route. Reachable at `/invite/:token` — the URL shared
 * from Trip Settings. Public entry point: any signed-in user can render it
 * (the token is the sharing secret; see firestore.rules § tripInvites).
 *
 * Flow:
 *   1. Fetch tripInvites/{token} (public read for signed-in users).
 *   2. Show trip card + Accept / Not now.
 *   3. On Accept → call the redeemInviteLink Cloud Function → navigate to /trip/:id.
 *
 * Visitors who aren't signed in are bounced to /auth with the invite path
 * preserved in location state (same pattern RequireAuth uses).
 */
export default function InviteRoute() {
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // While auth is initializing, don't decide anything yet.
  if (authLoading) return <BootLoader />;

  // Not signed in → bounce, preserving the invite URL so the user comes back
  // here after registering.
  if (!user) {
    return (
      <Navigate
        to="/auth"
        state={{ from: `/invite/${token ?? ""}` }}
        replace
      />
    );
  }

  if (!token) return <NotFound />;

  return <InviteAcceptPage token={token} onNavigate={navigate} />;
}

// ─── Loaded invite view ──────────────────────────────────────────────────────

function InviteAcceptPage({
  token,
  onNavigate,
}: {
  token: string;
  onNavigate: (to: string) => void;
}) {
  const query = useQuery({
    queryKey: queryKeys.inviteLink(token),
    queryFn: async () => {
      return unwrap(getInviteLink(token));
    },
  });

  const [redeemStarted, setRedeemStarted] = useState(false);
  const redeem = useMutation({
    mutationFn: async () => {
      const data = await unwrap(redeemInviteLink(token));
      return data.tripId;
    },
    onSuccess: (tripId) => {
      toast.success("You're in — welcome to the trip.");
      onNavigate(`/trip/${tripId}`);
    },
    onError: (err) => {
      setRedeemStarted(false);
      toast.error(err instanceof Error ? err.message : "Couldn't accept invite.");
    },
  });

  if (query.isLoading) return <BootLoader />;
  if (query.isError || !query.data) return <NotFound />;

  const link = query.data;
  const state = statusOf(link);
  const isDead = state !== "active";

  return (
    <main className="min-h-full bg-bg">
      <NavHeader title="Trip invite" showBack maxWidthClassName="max-w-md" />

      <div className="mx-auto flex max-w-md flex-col gap-5 px-5 pt-4">
        <TripCard link={link} />

        {isDead ? (
          <DeadLinkNotice state={state} />
        ) : (
          <RedeemPanel
            link={link}
            disabled={redeemStarted || redeem.isPending}
            onAccept={() => {
              setRedeemStarted(true);
              redeem.mutate();
            }}
            onDecline={() => onNavigate("/dashboard")}
          />
        )}
      </div>
    </main>
  );
}

// ─── Trip card ───────────────────────────────────────────────────────────────

function TripCard({ link }: { link: InviteLink }) {
  return (
    <motion.section
      initial={{ y: 12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative overflow-visible rounded-xl border border-line bg-bg-elevated shadow-warm-2"
    >
      {/*
        Soft warm glow behind the card — a blurred, animated halo that
        cycles slowly so the invite feels alive without being showy. Kept
        entirely inside the card's bounding box so it can't intercept taps.
      */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.28, 0.55, 0.28],
          scale: [1.02, 1.06, 1.02],
        }}
        transition={{
          duration: 4.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="pointer-events-none absolute -inset-1 -z-10 rounded-2xl blur-2xl"
        style={{
          background:
            "conic-gradient(from 210deg at 50% 50%, #E4572E 0deg, #F0902B 120deg, #17A5AE 240deg, #E4572E 360deg)",
        }}
      />
      <div className="overflow-hidden rounded-xl">
      {/* Cover strip */}
      <div className="relative aspect-[5/2] w-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg,#E4572E,#7A2712)" }}
          aria-hidden
        />
        {link.miniature ? (
          <img
            src={link.miniature}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(20,15,10,0.72) 0%, rgba(20,15,10,0.1) 60%)",
          }}
          aria-hidden
        />
        <div className="absolute inset-x-4 bottom-3 text-white">
          <h2
            className="font-display leading-tight m-0"
            style={{
              fontVariationSettings: `"opsz" 64, "wght" 500`,
              fontSize: "clamp(24px, 6.5vw, 30px)",
              letterSpacing: "-0.01em",
            }}
          >
            {link.tripName}
          </h2>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-1 px-4 py-4 text-[13px] text-ink">
        {link.city ? (
          <Row icon={MapPin} label={link.city} />
        ) : null}
        <Row
          icon={CalendarDays}
          label={formatDateRange(link.startDate, link.endDate)}
        />
        <Row
          icon={Users}
          label={
            link.maxAccepts != null
              ? `${Math.max(0, link.maxAccepts - link.acceptCount)} of ${link.maxAccepts} seats left`
              : "Open invite"
          }
        />
      </dl>
      </div>
    </motion.section>
  );
}

function Row({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2 py-1">
      <Icon className="h-4 w-4 text-ink-subtle" />
      <span>{label}</span>
    </div>
  );
}

// ─── Redeem panel ────────────────────────────────────────────────────────────

function RedeemPanel({
  link,
  disabled,
  onAccept,
  onDecline,
}: {
  link: InviteLink;
  disabled: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <motion.section
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.12, duration: 0.35, ease: "easeOut" }}
      className="flex flex-col gap-3 rounded-xl border border-line bg-bg-elevated p-5"
    >
      <p className="text-[14px] text-ink m-0">
        You've been invited to plan and join this trip
        {link.role === "viewer" ? " as a viewer" : ""}.
      </p>
      <p className="text-[12px] text-ink-subtle m-0">
        Accepting adds the trip to your dashboard. You can leave any time
        from Trip Settings.
      </p>
      <div className="mt-1 flex flex-col gap-2">
        {/*
          Idle micro-motion: a slow shine sweep across the button so the
          user's eye is drawn to the primary action without the button
          being noisy or bouncy. `whileTap` gives a tiny press feedback.
        */}
        <motion.div
          whileTap={{ scale: 0.98 }}
          className="relative overflow-hidden rounded-md"
        >
          <Button block size="lg" onClick={onAccept} disabled={disabled}>
            {disabled ? "Joining…" : "Join trip"}
          </Button>
          {!disabled ? (
            <motion.span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3"
              style={{
                background:
                  "linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)",
                mixBlendMode: "overlay",
              }}
              initial={{ x: "-100%" }}
              animate={{ x: "420%" }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                repeatDelay: 1.4,
                ease: "easeInOut",
              }}
            />
          ) : null}
        </motion.div>
        <Button block variant="ghost" onClick={onDecline} disabled={disabled}>
          Not now
        </Button>
      </div>
    </motion.section>
  );
}

// ─── Dead-link states ────────────────────────────────────────────────────────

type LinkState = "active" | "revoked" | "expired" | "full";

function statusOf(link: InviteLink): LinkState {
  if (link.revoked) return "revoked";
  if (link.expiresAt && link.expiresAt.toMillis() < Date.now()) return "expired";
  if (link.maxAccepts != null && link.acceptCount >= link.maxAccepts) return "full";
  return "active";
}

function DeadLinkNotice({ state }: { state: Exclude<LinkState, "active"> }) {
  const copy = {
    revoked: {
      title: "This invite was turned off",
      body: "Ask the person who shared this link to send you a new one.",
    },
    expired: {
      title: "This invite has expired",
      body: "Ask the person who shared this link to send you a fresh one.",
    },
    full: {
      title: "This invite is full",
      body: "The seat cap has been reached. Ask the trip owner for a new link.",
    },
  }[state];
  return (
    <section className="rounded-xl border border-line bg-bg-elevated p-5 text-center">
      <p className="font-display text-[20px] text-ink m-0" style={{ fontVariationSettings: `"opsz" 48, "wght" 500` }}>
        {copy.title}
      </p>
      <p className="mt-1 text-[13px] text-ink-muted m-0">{copy.body}</p>
    </section>
  );
}

// ─── Boot + not-found ────────────────────────────────────────────────────────

function BootLoader() {
  return (
    <main className="min-h-full bg-bg">
      <NavHeader title="Trip invite" showBack maxWidthClassName="max-w-md" />
      <div className="mx-auto flex max-w-md flex-col gap-4 px-5 pt-4">
        <Skeleton className="aspect-[5/2] w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    </main>
  );
}

function NotFound() {
  // Bring the redirect on error back to a useful place. Using useEffect
  // so we don't fire during render.
  const navigate = useNavigate();
  useEffect(() => {
    // Give the user a beat to read the empty state before we auto-bounce.
    const t = window.setTimeout(() => navigate("/dashboard"), 4000);
    return () => window.clearTimeout(t);
  }, [navigate]);
  return (
    <main className="min-h-full bg-bg">
      <NavHeader title="Trip invite" showBack maxWidthClassName="max-w-md" />
      <EmptyState
        icon={Users}
        title="Invite not found"
        body="This link may have been revoked or was never valid."
      />
    </main>
  );
}
